import { supabase } from './supabase';
import type {
  POBManifest,
  POBEntry,
  POBEntryInput,
  POBManifestStatus,
  POBColumnMapping,
} from '@/types/pob';

async function getAuthUserId(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.id) throw new Error('Not authenticated');
  return session.user.id;
}

async function getTenantId(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Not authenticated');
  const payload = JSON.parse(
    atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
  );
  const tenantId = payload.tenant_id as string | undefined;
  if (!tenantId) throw new Error('Tenant not resolved');
  return tenantId;
}

export async function getManifestsForProject(
  projectId: string,
  limit = 50
): Promise<POBManifest[]> {
  const { data, error } = await supabase
    .from('pob_manifests')
    .select('*, projects:projects(name, code)')
    .eq('project_id', projectId)
    .eq('is_deleted', false)
    .order('manifest_date', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as POBManifest[];
}

export async function getPublishedPOBManifestsForProject(
  projectId: string,
  limit: number = 20
): Promise<POBManifest[]> {
  const { data, error } = await supabase
    .from('pob_manifests')
    .select('*, projects:projects(name, code)')
    .eq('project_id', projectId)
    .eq('status', 'published')
    .eq('is_deleted', false)
    .order('manifest_date', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as POBManifest[];
}

export async function getManifest(manifestId: string): Promise<POBManifest> {
  const { data: manifest, error } = await supabase
    .from('pob_manifests')
    .select('*, projects:projects(name, code)')
    .eq('id', manifestId)
    .eq('is_deleted', false)
    .single();
  if (error) throw new Error(error.message);

  const { data: entries, error: entriesErr } = await supabase
    .from('pob_entries')
    .select('*')
    .eq('manifest_id', manifestId)
    .eq('is_deleted', false)
    .order('last_name', { ascending: true });
  if (entriesErr) throw new Error(entriesErr.message);

  return { ...(manifest as POBManifest), entries: (entries ?? []) as POBEntry[] };
}

// Why: idempotent create — one manifest per (project_id, manifest_date).
// Partial unique index on the table guarantees DB-level uniqueness; this
// returns the existing row if one is already present for that day.
export async function createManifest(
  projectId: string,
  manifestDate: string,
  facilityName?: string | null
): Promise<POBManifest> {
  const { data: existing } = await supabase
    .from('pob_manifests')
    .select('*, projects:projects(name, code)')
    .eq('project_id', projectId)
    .eq('manifest_date', manifestDate)
    .eq('is_deleted', false)
    .maybeSingle();
  if (existing) return existing as POBManifest;

  const tenantId = await getTenantId();
  const createdBy = await getAuthUserId();
  const { data, error } = await supabase
    .from('pob_manifests')
    .insert({
      tenant_id: tenantId,
      project_id: projectId,
      manifest_date: manifestDate,
      facility_name: facilityName ?? null,
      status: 'draft' as POBManifestStatus,
      total_pob: 0,
      created_by: createdBy,
    })
    .select('*, projects:projects(name, code)')
    .single();
  if (error) throw new Error(error.message);
  return data as POBManifest;
}

async function recomputeTotalPOB(manifestId: string): Promise<void> {
  const { count } = await supabase
    .from('pob_entries')
    .select('*', { count: 'exact', head: true })
    .eq('manifest_id', manifestId)
    .eq('is_deleted', false)
    .neq('status', 'departed');
  await supabase
    .from('pob_manifests')
    .update({ total_pob: count ?? 0, updated_at: new Date().toISOString() })
    .eq('id', manifestId);
}

export async function upsertEntry(
  manifestId: string,
  entry: POBEntryInput & { id?: string }
): Promise<POBEntry> {
  const { data: manifest, error: mErr } = await supabase
    .from('pob_manifests')
    .select('tenant_id, project_id')
    .eq('id', manifestId)
    .single();
  if (mErr) throw new Error(mErr.message);

  const createdBy = await getAuthUserId();

  if (entry.id) {
    const { data, error } = await supabase
      .from('pob_entries')
      .update({
        first_name: entry.first_name,
        last_name: entry.last_name,
        company: entry.company,
        position_title: entry.position_title,
        hitch_on_date: entry.hitch_on_date,
        hitch_off_date: entry.hitch_off_date ?? null,
        status: entry.status,
        is_sse: entry.is_sse,
        sse_context: entry.sse_context ?? null,
        sse_notes: entry.sse_notes ?? null,
      })
      .eq('id', entry.id)
      .select('*')
      .single();
    if (error) throw new Error(error.message);
    await recomputeTotalPOB(manifestId);
    return data as POBEntry;
  }

  const { data, error } = await supabase
    .from('pob_entries')
    .insert({
      tenant_id: (manifest as { tenant_id: string }).tenant_id,
      project_id: (manifest as { project_id: string }).project_id,
      manifest_id: manifestId,
      first_name: entry.first_name,
      last_name: entry.last_name,
      company: entry.company,
      position_title: entry.position_title,
      hitch_on_date: entry.hitch_on_date,
      hitch_off_date: entry.hitch_off_date ?? null,
      status: entry.status,
      is_sse: entry.is_sse,
      sse_context: entry.sse_context ?? null,
      sse_notes: entry.sse_notes ?? null,
      created_by: createdBy,
    })
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  await recomputeTotalPOB(manifestId);
  return data as POBEntry;
}

export async function bulkInsertEntries(
  manifestId: string,
  entries: POBEntryInput[]
): Promise<{ inserted: number; errors: { row: number; reason: string }[] }> {
  const { data: manifest, error: mErr } = await supabase
    .from('pob_manifests')
    .select('tenant_id, project_id')
    .eq('id', manifestId)
    .single();
  if (mErr) throw new Error(mErr.message);

  const createdBy = await getAuthUserId();
  const { tenant_id, project_id } = manifest as { tenant_id: string; project_id: string };

  const rows = entries.map((e) => ({
    tenant_id,
    project_id,
    manifest_id: manifestId,
    first_name: e.first_name,
    last_name: e.last_name,
    company: e.company,
    position_title: e.position_title,
    hitch_on_date: e.hitch_on_date,
    hitch_off_date: e.hitch_off_date ?? null,
    status: e.status,
    is_sse: e.is_sse,
    sse_context: e.sse_context ?? null,
    sse_notes: e.sse_notes ?? null,
    created_by: createdBy,
  }));

  const { data, error } = await supabase
    .from('pob_entries')
    .insert(rows)
    .select('id');
  if (error) {
    return {
      inserted: 0,
      errors: [{ row: 0, reason: error.message }],
    };
  }
  await recomputeTotalPOB(manifestId);
  return { inserted: data?.length ?? 0, errors: [] };
}

export async function deleteEntry(entryId: string, manifestId: string): Promise<void> {
  const { error } = await supabase
    .from('pob_entries')
    .update({ is_deleted: true, updated_at: new Date().toISOString() })
    .eq('id', entryId);
  if (error) throw new Error(error.message);
  await recomputeTotalPOB(manifestId);
}

export async function publishManifest(manifestId: string): Promise<void> {
  const { error } = await supabase
    .from('pob_manifests')
    .update({ status: 'published', updated_at: new Date().toISOString() })
    .eq('id', manifestId);
  if (error) throw new Error(error.message);
}

export async function archiveManifest(manifestId: string): Promise<void> {
  const { error } = await supabase
    .from('pob_manifests')
    .update({ status: 'archived', updated_at: new Date().toISOString() })
    .eq('id', manifestId);
  if (error) throw new Error(error.message);
}

// Carry forward a published manifest to a new date (typically today).
// Creates a fresh 'draft' manifest for toDate under the same project,
// copies over active entries (filtering out 'departed'), preserves
// hitch_on_date so the SSE trigger recomputes is_sse for the new day.
//
// Composes existing service functions (getManifest, createManifest,
// bulkInsertEntries) rather than reinventing them. Fails fast if a
// manifest already exists for (projectId, toDate) — SSR chooses
// whether to edit the existing one or archive it first.
export async function carryForwardManifest(
  fromManifestId: string,
  toDate: string,
  projectId: string,
): Promise<{ manifestId: string }> {
  const { data: existing } = await supabase
    .from('pob_manifests')
    .select('id')
    .eq('project_id', projectId)
    .eq('manifest_date', toDate)
    .eq('is_deleted', false)
    .maybeSingle();
  if (existing) {
    throw new Error(
      "Today's manifest already exists — edit it directly instead of carrying forward.",
    );
  }

  const src = await getManifest(fromManifestId);

  const newManifest = await createManifest(
    projectId,
    toDate,
    src.facility_name ?? null,
  );

  const carryEntries: POBEntryInput[] = (src.entries ?? [])
    .filter((e) => !e.is_deleted && e.status !== 'departed')
    .map((e) => ({
      first_name:     e.first_name,
      last_name:      e.last_name,
      company:        e.company,
      position_title: e.position_title,
      hitch_on_date:  e.hitch_on_date,
      hitch_off_date: e.hitch_off_date ?? null,
      status:         e.status,
      is_sse:         e.is_sse,
      sse_context:    e.sse_context ?? null,
      sse_notes:      e.sse_notes ?? null,
    }));

  if (carryEntries.length > 0) {
    await bulkInsertEntries(newManifest.id, carryEntries);
  }

  return { manifestId: newManifest.id };
}

export async function updateManifestHeader(
  manifestId: string,
  patch: { manifest_date?: string; facility_name?: string | null; notes?: string | null }
): Promise<void> {
  const { error } = await supabase
    .from('pob_manifests')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', manifestId);
  if (error) throw new Error(error.message);
}

// Autocomplete: distinct companies seen on prior manifests for this project.
export async function getCompanyAutocomplete(projectId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('pob_entries')
    .select('company')
    .eq('project_id', projectId)
    .eq('is_deleted', false)
    .limit(500);
  if (error) return [];
  const set = new Set<string>();
  (data ?? []).forEach((r) => {
    const c = (r as { company: string }).company;
    if (c) set.add(c);
  });
  return Array.from(set).sort();
}

// ─── Column mappings ──────────────────────────────────────────────────────

export async function getColumnMappings(): Promise<POBColumnMapping[]> {
  const { data, error } = await supabase
    .from('pob_column_mappings')
    .select('*')
    .eq('module', 'pob')
    .order('is_default', { ascending: false })
    .order('updated_at', { ascending: false });
  if (error) return [];
  return (data ?? []) as POBColumnMapping[];
}

export async function getDefaultColumnMapping(): Promise<POBColumnMapping | null> {
  const { data, error } = await supabase
    .from('pob_column_mappings')
    .select('*')
    .eq('module', 'pob')
    .eq('is_default', true)
    .maybeSingle();
  if (error) return null;
  return (data ?? null) as POBColumnMapping | null;
}

// Why: partial unique index enforces one default mapping per (tenant, module).
// We clear the previous default for this module before inserting a new default row.
export async function saveColumnMapping(params: {
  name: string;
  columnMap: Record<string, string>;
  makeDefault?: boolean;
}): Promise<POBColumnMapping> {
  const tenantId = await getTenantId();
  const createdBy = await getAuthUserId();

  if (params.makeDefault) {
    await supabase
      .from('pob_column_mappings')
      .update({ is_default: false, updated_at: new Date().toISOString() })
      .eq('tenant_id', tenantId)
      .eq('module', 'pob')
      .eq('is_default', true);
  }

  const { data, error } = await supabase
    .from('pob_column_mappings')
    .insert({
      tenant_id: tenantId,
      mapping_name: params.name,
      column_map: params.columnMap,
      is_default: !!params.makeDefault,
      module: 'pob',
      created_by: createdBy,
    })
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  return data as POBColumnMapping;
}

// ─── DSR Workforce integration ────────────────────────────────────────────

export async function getTodayPublishedManifest(
  projectId: string,
  date?: string
): Promise<POBManifest | null> {
  const today =
    date ?? new Date().toISOString().slice(0, 10);
  const { data, error } = await supabase
    .from('pob_manifests')
    .select('*, projects:projects(name, code)')
    .eq('project_id', projectId)
    .eq('manifest_date', today)
    .eq('status', 'published')
    .eq('is_deleted', false)
    .maybeSingle();
  if (error) return null;
  return (data ?? null) as POBManifest | null;
}

// Any-status lookup used by the Workforce form so that a draft POB on
// the DSR's report_date can still seed the crew list — pilots are rarely
// disciplined about publishing the manifest before the SSR starts the DSR.
// High-SSE alerting still requires 'published' (see alert-engine).
export async function getTodayManifest(
  projectId: string,
  date?: string,
): Promise<POBManifest | null> {
  const today = date ?? new Date().toISOString().slice(0, 10);
  const { data, error } = await supabase
    .from('pob_manifests')
    .select('*, projects:projects(name, code)')
    .eq('project_id', projectId)
    .eq('manifest_date', today)
    .eq('is_deleted', false)
    .in('status', ['draft', 'published'])
    .order('status', { ascending: false }) // 'published' before 'draft'
    .limit(1)
    .maybeSingle();
  if (error) return null;
  return (data ?? null) as POBManifest | null;
}

// Returns today's published manifests for the given projects.
// Used by SupervisorDashboard for the "Active Workforce" / "SSE Coverage"
// stat cards — supervisors may have multiple projects in scope.
export async function getTodayPublishedManifestForProjects(
  projectIds: string[],
  date?: string,
): Promise<POBManifest[]> {
  if (projectIds.length === 0) return [];
  const today = date ?? new Date().toISOString().slice(0, 10);
  const { data, error } = await supabase
    .from('pob_manifests')
    .select('*, projects:projects(name, code)')
    .in('project_id', projectIds)
    .eq('manifest_date', today)
    .eq('status', 'published')
    .eq('is_deleted', false);
  if (error) return [];
  return (data ?? []) as POBManifest[];
}

// Aggregates total POB and SSE counts across one or more published
// manifests. Pulls entries in a single query for efficiency.
export interface POBSupervisorStats {
  manifestCount: number;
  totalPOB: number;
  sseCount: number;
  ssePercent: number;
}

export async function getPOBStatsForManifests(
  manifestIds: string[],
): Promise<POBSupervisorStats> {
  if (manifestIds.length === 0) {
    return { manifestCount: 0, totalPOB: 0, sseCount: 0, ssePercent: 0 };
  }
  const { data, error } = await supabase
    .from('pob_entries')
    .select('is_sse, status')
    .in('manifest_id', manifestIds)
    .eq('is_deleted', false)
    .neq('status', 'departed');
  if (error) {
    return { manifestCount: manifestIds.length, totalPOB: 0, sseCount: 0, ssePercent: 0 };
  }
  const rows = (data ?? []) as { is_sse: boolean; status: string }[];
  const totalPOB = rows.length;
  const sseCount = rows.filter((r) => r.is_sse).length;
  const ssePercent = totalPOB > 0 ? Math.round((sseCount / totalPOB) * 1000) / 10 : 0;
  return { manifestCount: manifestIds.length, totalPOB, sseCount, ssePercent };
}

export interface WorkforceFromPOB {
  company: string;
  entries: POBEntry[];
  totalCount: number;
  sseCount: number;
  ssePercentage: number;
}

export async function getPOBEntriesForWorkforce(
  manifestId: string
): Promise<WorkforceFromPOB[]> {
  const { data, error } = await supabase
    .from('pob_entries')
    .select('*')
    .eq('manifest_id', manifestId)
    .eq('is_deleted', false)
    .neq('status', 'departed')
    .order('company', { ascending: true })
    .order('last_name', { ascending: true });
  if (error) throw new Error(error.message);

  const groups = new Map<string, POBEntry[]>();
  (data ?? []).forEach((e) => {
    const entry = e as POBEntry;
    const list = groups.get(entry.company) ?? [];
    list.push(entry);
    groups.set(entry.company, list);
  });

  return Array.from(groups.entries()).map(([company, entries]) => {
    const sseCount = entries.filter((e) => e.is_sse).length;
    return {
      company,
      entries,
      totalCount: entries.length,
      sseCount,
      ssePercentage:
        entries.length > 0 ? Math.round((sseCount / entries.length) * 100) : 0,
    };
  });
}

export interface SSEByProjectRow {
  projectId: string;
  projectName: string;
  hasPublishedManifest: boolean;
  totalPOB: number;
  sseCount: number;
  ssePercent: number;
}

// Per-project SSE summary for the given date (defaults to today). Looks
// up the published manifest per project, then aggregates entry SSE
// counts. Projects without a published manifest are returned with
// hasPublishedManifest=false and zero counts so dashboards can render
// the gap explicitly.
//
// Used by the Executive Workforce summary, the Safety Manager SSE
// distribution row, and the Supervisor SSE Summary Today widget — each
// of those reduces this row set to its own headline shape.
export async function getSSEByProject(
  projectIds: string[],
  date?: string,
): Promise<SSEByProjectRow[]> {
  if (projectIds.length === 0) return [];
  const today = date ?? new Date().toISOString().slice(0, 10);

  const [projRes, manifestRes] = await Promise.all([
    supabase
      .from('projects')
      .select('id, name')
      .in('id', projectIds)
      .eq('is_deleted', false)
      .order('name'),
    supabase
      .from('pob_manifests')
      .select('id, project_id')
      .in('project_id', projectIds)
      .eq('manifest_date', today)
      .eq('status', 'published')
      .eq('is_deleted', false),
  ]);
  if (projRes.error) throw new Error(projRes.error.message);
  if (manifestRes.error) throw new Error(manifestRes.error.message);

  const projects = (projRes.data ?? []) as { id: string; name: string }[];
  const manifestByProject = new Map<string, string>();
  for (const m of (manifestRes.data ?? []) as { id: string; project_id: string }[]) {
    manifestByProject.set(m.project_id, m.id);
  }
  const manifestIds = Array.from(manifestByProject.values());

  let entries: { manifest_id: string; is_sse: boolean; status: string }[] = [];
  if (manifestIds.length > 0) {
    const { data, error } = await supabase
      .from('pob_entries')
      .select('manifest_id, is_sse, status')
      .in('manifest_id', manifestIds)
      .eq('is_deleted', false)
      .neq('status', 'departed');
    if (error) throw new Error(error.message);
    entries = (data ?? []) as typeof entries;
  }

  const aggByManifest = new Map<string, { totalPOB: number; sseCount: number }>();
  for (const e of entries) {
    const cur = aggByManifest.get(e.manifest_id) ?? { totalPOB: 0, sseCount: 0 };
    cur.totalPOB += 1;
    if (e.is_sse) cur.sseCount += 1;
    aggByManifest.set(e.manifest_id, cur);
  }

  return projects.map((p) => {
    const manifestId = manifestByProject.get(p.id);
    const agg = manifestId ? aggByManifest.get(manifestId) ?? { totalPOB: 0, sseCount: 0 } : null;
    const totalPOB = agg?.totalPOB ?? 0;
    const sseCount = agg?.sseCount ?? 0;
    return {
      projectId: p.id,
      projectName: p.name,
      hasPublishedManifest: !!manifestId,
      totalPOB,
      sseCount,
      ssePercent: totalPOB > 0 ? Math.round((sseCount / totalPOB) * 1000) / 10 : 0,
    };
  });
}

// List projects visible to the current user (for POB Home project picker).
export async function getVisibleProjects(): Promise<
  { id: string; name: string; code: string | null }[]
> {
  const { data, error } = await supabase
    .from('projects')
    .select('id, name, code')
    .eq('is_deleted', false)
    .order('name');
  if (error) throw new Error(error.message);
  // Coerce null/empty name → code → short-id fallback so the picker
  // can never display a raw UUID even if a row arrives without a name.
  return (data ?? []).map((p: { id: string; name: string | null; code: string | null }) => ({
    id: p.id,
    name: p.name && p.name.trim().length > 0
      ? p.name
      : (p.code ?? `Project ${p.id.slice(0, 8)}`),
    code: p.code,
  }));
}
